import { useCountByStatus } from "@/api/bibliography/bibliography";
import { PagingParameters } from "@/api/dtos";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { Bibliography } from "@/lib/types";
import { ColumnDef } from "@tanstack/react-table";
import { ChevronLeft, PlusCircle, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * BibliographyLinks component
 * @returns {JSX.Element}
 */
const BibliographyLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [
    getPaginationRelatedBibliographies,
    setPaginationRelatedBibliographies,
  ] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedBibliography",
    getPaginationRelatedBibliographies.pageSize,
    getPaginationRelatedBibliographies.page *
      getPaginationRelatedBibliographies.pageSize,
  );

  const { data: countRelatedBibliographies } = useCountRelatedFields(id, type, [
    "relatedBibliography",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedBibliography",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedBibliography",
  );

  const [searchContent, setSearchContent] = useState<string>("");

  const [isAddMode, setIsAddMode] = useState(false);

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const { data: countByStatus } = useCountByStatus();
  const totalSize = countByStatus?.published + countByStatus?.draft;

  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resultType: "bibliography",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteRelation(id);
    },
    [deleteRelation],
  );

  const columns: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographiesDescriptionNumeroBibliographie"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.bibliographieDescriptionNumeroBibliographie}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"bibliography"}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionTitre",
        header: t("tab_bibliographieDescriptionTitre"),
        size: 150,
        cell: (props: any) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "bibliographieDescriptionAnnee",
        header: t("bibliographiesDescriptionAnnee"),
        size: 100,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.bibliographieDescriptionAnnee} />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          if (isAddMode) {
            return (
              <div className="flex items-center gap-1 justify-end mr-2">
                <ShowElements
                  type="bibliography"
                  id={row.original.id}
                  elements={[
                    {
                      label: t("bibliographieDescriptionNumeroBibliographie"),
                      value:
                        row.original
                          .bibliographieDescriptionNumeroBibliographie,
                      type: "text",
                    },
                    {
                      label: t("bibliographieDescriptionResumeLong"),
                      value: row.original.bibliographieDescriptionResumeLong,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.attachments,
                      type: "image",
                    },
                  ]}
                  onAdd={() => handleCreateRelation(row.original.id)}
                />
                <Button
                  className="font-semibold flex gap-2 p-1"
                  variant="ghost"
                  onClick={() => handleCreateRelation(row.original.id)}
                  type="button"
                >
                  <PlusCircle className="w-5 h-5 cursor-pointer text-[#005D8D]" />
                </Button>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(row.original.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <ShowElements
                type="bibliography"
                id={row.original.id}
                elements={[
                  {
                    label: t("bibliographieDescriptionNumeroBibliographie"),
                    value:
                      row.original.bibliographieDescriptionNumeroBibliographie,
                    type: "text",
                  },
                  {
                    label: t("bibliographieDescriptionResumeLong"),
                    value: row.original.bibliographieDescriptionResumeLong,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          );
        },
      },
    ];
  }, [handleCreateRelation, handleDeleteRelation, isAddMode, t]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between mb-2">
        <h2 className="text-lg font-semibold">{t("linkedBibliographies")}</h2>
        {!isAddMode && (
          <Button variant="outline" onClick={handleAddMode}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>

      <DataTable
        columns={columns}
        data={
          isAddMode
            ? (notRelatedBibliographies?.data ?? [])
            : (relatedFields ?? [])
        }
        pagination={
          isAddMode
            ? {
                pagination: getPagination,
                setPage: setPagination,
                totalSize: totalSize,
              }
            : {
                pagination: getPaginationRelatedBibliographies,
                setPage: setPaginationRelatedBibliographies,
                totalSize: countRelatedBibliographies?.relatedBibliography,
              }
        }
        options={{
          enableColumnResizing: true,
        }}
      />
    </div>
  );
};

export default BibliographyLinks;
